<script setup lang="ts">
import type { NavItemProps } from "~/types/nav"

const props = defineProps<NavItemProps>()

const personalization = usePersonalizationStore()

const toggleReadArticle: Ref<boolean | null> = ref(null)

const result = personalization.readHistory?.find(
  ({ id }) => id === props.item.id,
)
toggleReadArticle.value = result?.done ?? false
const totalArticlesDone = computed(() => {
  return personalization.readHistory?.filter(
    i => i.done && props.item.id === i.subjectId,
  ).length
})

const chapterStatus = computed(() => {
  if (!props.item.totalChildren || totalArticlesDone.value === 0) {
    return "not-started"
  } else if (props.item.totalChildren > totalArticlesDone.value) {
    return "in-progress"
  } else {
    return "done"
  }
})

const articleStatus = computed(() => {
  if (
    personalization.readHistory.filter(
      psItem => psItem.done && psItem.id === props.item.id,
    ).length > 0
  ) {
    return true
  }
  return false
})

const contentStore = useProductContentStore()

const subject = computed(() =>
  contentStore.subjects.find(
    subject => subject.title === props.item.text,
  ),
)

const mediaCounts = useMediaCounts(subject)

const subjectHasContent = useHasContent(
  mediaCounts.data,
  () => !!subject.value?.introPages.length,
)

const isOnline = useOnline()
</script>

<template>
  <div
    class="select-none border-b-2 border-white/20"
    :class="[pageType === 'subject' ? 'flex items-start ' : '']"
  >
    <NuxtLink
      v-if="!item.hidden && item.frontendHref"
      :id="`anchor-element-${item.id}`"
      :to="item.frontendHref"
      :data-pendo="
        pageType === 'product'
          ? 'subject-list-item'
          : 'article-list-item'
      "
      class="group flex grow items-start gap-x-2.5 p-3"
      :class="[
        !subjectHasContent && pageType === 'product'
          ? 'pointer-events-none cursor-default text-grey-40'
          : 'cursor-pointer',
        toggleReadArticle ? 'border-opacity-50' : 'border-opacity-20',
        pageType !== 'product' ? 'ml-5' : '',
        pageType === 'subject' && item.restricted
          ? 'pointer-events-none text-grey-40'
          : '',
      ]"
    >
      <span
        v-if="pageType === 'product'"
        class="align-self-baseline flex items-center justify-center rounded font-national2 text-xl leading-7 group-hover:text-u-contrast"
      >
        {{ item.priority }}
      </span>
      <div class="flex h-fit w-full justify-between gap-x-3">
        <span
          class="font-national2 text-xl leading-7"
          :class="[
            pageType === 'product'
              ? 'group-hover:border-[--theme-medium] group-hover:text-u-contrast'
              : 'group-hover:border-u-contrast group-hover:text-u-contrast',
          ]"
        >
          {{ item.text }}
        </span>
        <span
          v-if="pageType === 'product' && licence && isOnline"
          class="ml-auto flex items-center justify-center"
        >
          <UChapterProgress
            v-if="item.totalChildren !== 0"
            :total-articles-done="totalArticlesDone"
            :total-children="item.totalChildren"
            :chapter-status="chapterStatus"
          />
        </span>
        <UContentFreeLabel
          v-if="pageType === 'product' && item.showFreeLabel"
          :small="true"
          class="self-center"
        />
      </div>
      <div
        v-if="
          isOnline &&
            licence &&
            !item.hidden &&
            pageType === 'subject'
        "
        class="mt-1 opacity-75 group-hover:text-white group-hover:opacity-100"
      >
        <UArticleStatusIndicator :done="articleStatus" />
      </div>
    </NuxtLink>
  </div>
</template>
